import React, { useEffect, useRef, useState, useCallback } from "react";
import { X } from "lucide-react";
import Message from "../../assets/icons/popup/message-circle-warning.svg?react";
import Gcash from "../../assets/icons/popup/GCASH-Logo.svg?react";
import Maya from "../../assets/icons/popup/MAYA-Logo.svg?react";
import Landmark from "../../assets/icons/popup/landmark.svg?react";
import QRPHLogo from "../../assets/icons/popup/QR_Ph_Logo.svg?react";
import BgLatto from "../../assets/images/bg-latto.jpg";
import { useWebSocket } from "../../context/WebSocketContext";
import payApi from "../../api/pay";
import { useAuth } from "../../context/AuthContext";
import Swal from "sweetalert2";
import { QRCodeSVG } from "qrcode.react";

export default function DepositSuccessPopup({ amount, onClose, paymentSuccessData }) {
  const { paymentStatus, resetPaymentStatus, refreshWalletBalance } = useWebSocket();
  const { tokens } = useAuth();
  const [isCheckingPayment, setIsCheckingPayment] = useState(false);
  const [isPaymentSuccess, setIsPaymentSuccess] = useState(false);
  
  // Use refs for values that don't need to trigger re-renders
  const isPollingRef = useRef(true);
  const pollIntervalRef = useRef(null);
  
  // Store payment IDs for easier access
  const paymentId = paymentSuccessData?.paymentId;
  const paymentRequestId = paymentSuccessData?.payment_request_id;
  const merchantOrderId = paymentSuccessData?.merchantOrderId; // For Philippines wallet
  const isMayaPayment = paymentSuccessData?.gateway === 'maya';
  const isPhmpayPayment = paymentSuccessData?.gateway === 'phmpay';
  const qrCodeData = paymentSuccessData?.qrCode || paymentSuccessData?.qrcodeRaw; // Support both fields

  // Helper function to determine if QR code is base64 image or text string
  const isBase64Image = (qrCode) => {
    if (!qrCode) return false;
    
    // Check for QR payment text patterns (like GCash and Maya format starting with "00020101...")
    if (qrCode.startsWith('000201') || qrCode.startsWith('00020101')) {
      return false; // This is QR payment text, not an image
    }
    
    // Check if it's a base64 string (data URI or common image headers)
    return (
      qrCode.startsWith('data:image/') ||
      qrCode.startsWith('iVBORw0KGgo') || // PNG header in base64
      qrCode.startsWith('/9j/4AAQ') || // JPEG header in base64
      qrCode.startsWith('UklGRg==') // WEBP header in base64
    );
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      isPollingRef.current = false;
    };
  }, []);

  // Show success alert and clean up
  const showSuccessAlert = useCallback(() => {
    // Stop polling
    isPollingRef.current = false;
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    
    setIsPaymentSuccess(true);
    
    console.log("Showing success alert now!");
    
    // Force with a small timeout to ensure UI updates first
    setTimeout(() => {
      try {
        Swal.fire({
          title: 'Payment Successful',
          text: 'Your payment has been confirmed!',
          icon: 'success',
          allowOutsideClick: false,
          showConfirmButton: true,
          confirmButtonText: "OK",
          confirmButtonColor: "#0E64D1",
          background: "#1e3a8a",
          color: "#fff",
          fontFamily: "Poppins",
          fontSize: "16px",
          fontWeight: "bold",
          textAlign: "center",
          padding: "10px 20px",
          borderRadius: "10px",
        }).then((result) => {
          console.log("Sweet alert closed with result:", result);
          refreshWalletBalance();
          resetPaymentStatus();
          onClose();
        });
      } catch (err) {
        console.error("Error showing success alert:", err);
        // Fallback to ensure we still close even if alert fails
        refreshWalletBalance();
        resetPaymentStatus();
        onClose();
      }
    }, 100);
  }, [refreshWalletBalance, resetPaymentStatus, onClose]);

  // Check payment status function (different for Maya vs Philippines wallet vs other payments)
  const checkPaymentStatus = useCallback(async () => {
    if (!isPollingRef.current || isPaymentSuccess) return;
    
    if (!tokens?.access?.token) {
      console.error("Missing auth token for payment check");
      return;
    }
    
    // Validate required fields based on payment type
    if (isMayaPayment && !paymentId) {
      console.error("Missing paymentId for Maya payment");
      return;
    } else if (isPhmpayPayment && !merchantOrderId) {
      console.error("Missing merchantOrderId for Philippines wallet payment");
      return;
    } else if (!isMayaPayment && !isPhmpayPayment && (!paymentId || !paymentRequestId)) {
      console.error("Missing required data for non-Maya/non-Phmpay payment");
      return;
    }
    
    try {
      setIsCheckingPayment(true);
      
      let response;
      
      if (isMayaPayment) {
        // Use the Maya payment status endpoint
        console.log("Checking Maya payment status with paymentId:", paymentId);
        response = await payApi.checkMayaPaymentStatus(
          tokens.access.token,
          paymentId
        );
        
        console.log("Maya payment status response:", response.data);
        
        if (!isPollingRef.current) return; // Check if component is still mounted
        
        // Check for Maya payment completion
        const isMayaCompleted = response.status === 200 && (
          response.data.status === "PAYMENT_SUCCESS" ||
          response.data.isPaid === true
        );
        
        if (isMayaCompleted) {
          console.log("Maya payment completed! Calling showSuccessAlert()");
          setTimeout(() => {
            showSuccessAlert();
          }, 0);
        }
      } else if (isPhmpayPayment) {
        // Use the Philippines wallet payment status endpoint
        console.log("Checking Philippines wallet payment status with merchantOrderId:", merchantOrderId);
        response = await payApi.checkPhmpayPaymentStatus(
          tokens.access.token,
          merchantOrderId
        );
        
        console.log("Philippines wallet payment status response:", response.data);
        
        if (!isPollingRef.current) return; // Check if component is still mounted
        
        // Check for Philippines wallet payment completion
        const isPhmpayCompleted = response.status === 200 && response.data.success && (
          response.data.data?.status === "completed" ||
          response.data.data?.isPaid === true
        );
        
        if (isPhmpayCompleted) {
          console.log("Philippines wallet payment completed! Calling showSuccessAlert()");
          setTimeout(() => {
            showSuccessAlert();
          }, 0);
        }
      } else {
        // Use original webhook-confirm endpoint for other payments
        const cleanPaymentId = paymentId.toString().trim().replace(/\s/g, '');
        const cleanRequestId = paymentRequestId.toString().trim().replace(/\s/g, '');
        
        console.log("Checking non-Maya/non-Phmpay payment status with IDs:", {
          paymentId: cleanPaymentId,
          requestId: cleanRequestId
        });
        
        response = await payApi.checkPaymentStatus(
          tokens.access.token,
          cleanPaymentId,
          cleanRequestId
        );
        
        console.log("Non-Maya/non-Phmpay payment status response:", response.data);
        
        if (!isPollingRef.current) return; // Check if component is still mounted
        
        // More liberal check for completed status
        const isCompleted = 
          (response.data.status === "COMPLETED" || 
           response.data.status === "completed" || 
           response.data.status?.toLowerCase() === "completed" ||
           (response.data.success === true && response.data.data?.status === "completed"));
        
        if (response.status === 200 && isCompleted) {
          console.log("Non-Maya/non-Phmpay payment completed! Calling showSuccessAlert()");
          setTimeout(() => {
            showSuccessAlert();
          }, 0);
        }
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
    } finally {
      setIsCheckingPayment(false);
    }
  }, [paymentId, paymentRequestId, merchantOrderId, tokens, isPaymentSuccess, showSuccessAlert, isMayaPayment, isPhmpayPayment]);

  // Handle WebSocket payment status
  useEffect(() => {
    if (paymentStatus === "completed") {
      console.log("WebSocket received completed payment status!");
      showSuccessAlert();
    }
  }, [paymentStatus, showSuccessAlert]);

  // Start polling when component mounts
  useEffect(() => {
    // Check required IDs based on payment type
    if (isMayaPayment) {
      // For Maya payments, only paymentId is required
      if (!paymentId) {
        console.warn("Missing paymentId for Maya payment, not starting polling");
        return;
      }
      console.log("Starting Maya payment polling with paymentId:", paymentId);
    } else {
      // For non-Maya payments, both paymentId and paymentRequestId are required
      if (!paymentId || !paymentRequestId) {
        console.warn("Missing required payment IDs for non-Maya payment, not starting polling", {
          paymentId,
          paymentRequestId
        });
        return;
      }
      console.log("Starting non-Maya payment polling with IDs:", { paymentId, paymentRequestId });
    }
    
    console.log("Starting payment polling with interval");
    isPollingRef.current = true;
    
    // Initial check
    checkPaymentStatus();
    
    // Setup interval - using ref to avoid recreating interval
    pollIntervalRef.current = setInterval(() => {
      if (isPollingRef.current) {
        checkPaymentStatus();
      }
    }, 2000);
    
    // Setup visibility change listener
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Immediately check when tab becomes visible
        console.log("Tab visible, checking payment status");
        checkPaymentStatus();
      }
    };
    
    // Setup focus listener
    const handleFocus = () => {
      console.log("Window focused, checking payment status");
      checkPaymentStatus();
    };
    
    // Add direct iOS app switching detection
    const handlePageShow = () => {
      console.log("pageshow event triggered - iOS app switch detected");
      checkPaymentStatus();
    };
    
    // Add event listeners for visibility and focus
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pageshow', handlePageShow);
    
    // Cleanup
    return () => {
      isPollingRef.current = false;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [paymentId, paymentRequestId, merchantOrderId, isMayaPayment, isPhmpayPayment, checkPaymentStatus]);

  // Close handler
  const handleClose = () => {
    isPollingRef.current = false;
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    resetPaymentStatus();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="relative w-full max-w-sm text-white rounded-3xl overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{
            backgroundImage: `url('${BgLatto}'), linear-gradient(108.9deg, #1E429A 0%, #1E429A 100.79%)`,
            backgroundBlendMode: "multiply",
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(8px)",
          }}
        ></div>

        <div className="relative">
          <button
            onClick={handleClose}
            className="absolute right-2 top-2 text-gray-300 hover:text-white p-3 -mr-2 -mt-2 z-50"
          >
            <X className="w-5 h-5" />
          </button>

          {/* <div className="flex items-start gap-2 py-4 px-10 text-base leading-none font-normal">
            <Message height={70} width={70} />
            <p className="text-sm">You can close this window after making your payment. You will be notified once the payment has been successful.</p>
          </div> */}

          <div className="px-4 py-4">
            <h2 className="text-xl font-semibold text-center my-2 leading-none">
              Wallets and Banks are
              <br />
              accepted here
            </h2>

            <div className="relative my-4 border w-1/2 text-center p-2 rounded-md mx-auto">
              <div className="absolute inset-0 left-0 -top-2 text-xs">
                <span className="bg-[#1a237e] px-1"> Payment Amount </span>
              </div>
              <div className="text-xl font-bold">P{amount}</div>
            </div>

            <div className="relative z-10 bg-white p-2 rounded-lg w-[280px] mx-auto shadow-md mb-4">
              {qrCodeData ? (
                isBase64Image(qrCodeData) ? (
                  // Display base64 image (for QRPH)
                  <div className="relative">
                    <img 
                      src={qrCodeData.startsWith('data:') 
                        ? qrCodeData 
                        : `data:image/png;base64,${qrCodeData}`
                      } 
                      alt="QR Code" 
                      className="w-full h-auto"
                    />
                    {isMayaPayment && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-white rounded-lg p-1 shadow-md">
                          <QRPHLogo className="w-8 h-8" />
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  // Generate QR code from text string (for GCash and Maya)
                  <div className="relative flex justify-center">
                    <QRCodeSVG 
                      value={qrCodeData} 
                      size={256}
                      level="M"
                      includeMargin={true}
                    />
                    {isMayaPayment && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-white rounded-lg p-1 shadow-md">
                          <QRPHLogo className="w-8 h-8" />
                        </div>
                      </div>
                    )}
                  </div>
                )
              ) : (
                // Fallback if no QR code data
                <div className="flex items-center justify-center h-64 text-gray-500">
                  <p>QR Code not available</p>
                </div>
              )}
              <div className="text-center py-2 px-3 mt-2 rounded-lg bg-gray-100">
                <p className="text-gray-700 text-sm font-medium">
                  {isPaymentSuccess 
                    ? '✓ Payment confirmed!'
                    : isMayaPayment || isPhmpayPayment
                      ? 'Monitoring payment in background...'
                      : isCheckingPayment 
                        ? 'Waiting for payment confirmation...' 
                        : 'Waiting for payment confirmation...'}
                </p>
                {(isMayaPayment || isPhmpayPayment) && (
                  <p className="text-gray-500 text-xs mt-1">
                    You can close this window. We'll notify you when payment is complete.
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="relative -mt-4 z-0 flex items-center justify-center gap-8 bg-white py-4 pt-10 rounded-b-3xl border border-[#CE1F28] border-t-8">
            <Maya />
            <Landmark />
            <Gcash />
          </div>
        </div>
      </div>
    </div>
  );
}
